
import React from 'react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Service } from '@/lib/data';
import { SelectedServiceState } from '@/types/serviceTypes';

interface ServiceGroup {
  parent: Service;
  children: Service[];
  isParentSelectable?: boolean;
}

interface ServiceSelectionListProps {
  groups: ServiceGroup[];
  selectedServices: Record<string, SelectedServiceState>;
  toggleServiceSelection: (serviceId: string) => void;
  updateServicePrice: (serviceId: string, price: number | null) => void;
}

const ServiceSelectionList: React.FC<ServiceSelectionListProps> = ({
  groups,
  selectedServices,
  toggleServiceSelection,
  updateServicePrice
}) => {
  // Debug which services are selected
  React.useEffect(() => {
    console.log("Selected services in selection list:", 
      Object.entries(selectedServices)
        .filter(([_, state]) => state && state.selected)
        .map(([id]) => id)
    );
  }, [selectedServices]);

  const handlePriceChange = (serviceId: string, value: string) => {
    if (value === '') {
      updateServicePrice(serviceId, null);
    } else {
      const parsedValue = parseFloat(value);
      if (!isNaN(parsedValue)) {
        updateServicePrice(serviceId, parsedValue);
      }
    }
  };

  return (
    <ScrollArea className="flex-1" style={{ maxHeight: 'calc(60vh - 200px)', overflow: 'auto' }}>
      <div className="space-y-3 pr-4">
        {groups.map(group => (
          <div key={group.parent.service_id} className="border rounded-md overflow-hidden">
            <div className={`p-3 border-b flex items-start gap-3 ${
              group.isParentSelectable !== false ? 'bg-background' : 'bg-muted/50'
            }`}>
              {group.isParentSelectable !== false ? (
                <>
                  <Checkbox 
                    id={`service-${group.parent.service_id}`}
                    checked={selectedServices[group.parent.service_id]?.selected || false}
                    onCheckedChange={(checked) => {
                      toggleServiceSelection(group.parent.service_id);
                    }}
                    className="mt-1"
                  />
                  <div className="flex-grow">
                    <label 
                      htmlFor={`service-${group.parent.service_id}`}
                      className="font-medium cursor-pointer"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {group.parent.service_name}
                    </label>
                    <div className="mt-1 flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                      <span className="text-sm">Price:</span>
                      <Input
                        type="number"
                        step="0.01"
                        className="w-20 h-7 text-sm"
                        value={selectedServices[group.parent.service_id]?.price ?? ''}
                        onChange={(e) => handlePriceChange(
                          group.parent.service_id, 
                          e.target.value
                        )}
                        placeholder={group.parent.default_price.toString()}
                      />
                    </div>
                  </div>
                </>
              ) : (
                <div className="flex-grow">
                  <div className="font-medium text-muted-foreground">
                    {group.parent.service_name} (already added)
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    Additional services available:
                  </div>
                </div>
              )}
            </div>
            
            {group.children.length > 0 && (
              <div className="bg-muted/30">
                {group.children.map(child => (
                  <div 
                    key={child.service_id} 
                    className="p-3 border-t flex items-start gap-3 pl-6"
                  >
                    <Checkbox 
                      id={`service-${child.service_id}`}
                      checked={selectedServices[child.service_id]?.selected || false}
                      onCheckedChange={(checked) => {
                        toggleServiceSelection(child.service_id);
                      }}
                      className="mt-1"
                    />
                    <div className="flex-grow">
                      <label 
                        htmlFor={`service-${child.service_id}`}
                        className="font-medium cursor-pointer"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {child.service_name}
                      </label>
                      <div className="mt-1 flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                        <span className="text-sm">Price:</span>
                        <Input
                          type="number"
                          step="0.01"
                          className="w-20 h-7 text-sm"
                          value={selectedServices[child.service_id]?.price ?? ''}
                          onChange={(e) => handlePriceChange(
                            child.service_id, 
                            e.target.value
                          )}
                          placeholder={child.default_price.toString()}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </ScrollArea>
  );
};

export default ServiceSelectionList;
